import { useState, type ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { apiFetch } from '../../../lib/api-client';
import { API_BASE } from '../../../lib/api-base';
import { rereadAfterOwnerAction } from '../../../lib/owner-note-rereads';
import { stageBiValue, type OutreachStage } from '../../../lib/outreach-stage';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { TextButton } from '../ui';
import styles from './outreach-thread.module.css';

/**
 * **Close** — the owner finishing a conversation themselves. Replaces both "No reply needed" (the task
 * modal) and "Mark as replied" (the contact viewer): one action, one string (`outreach.close`), on every
 * surface that shows a thread.
 *
 * It writes `/api/outreach-complete`, which records `closed_by = OWNER` — so the thread reads "Closed by
 * you" afterwards (`outreach.line.closedByYou`). **Nothing is optimistic**: it writes, re-reads, and only
 * then dismisses (`onClosed`), because the surface the reader returns to must already show the thread
 * Done rather than claim a state that is not true yet. A failure keeps the surface open and says so
 * (`outreach.close.failed`).
 *
 * Hidden when the thread is already Done — there is nothing left to close. Not destructive and asks for
 * no confirmation: if they write again, the new message reopens the thread.
 *
 * BI `outreach_close` (it replaces the old "no reply needed" event) with `additionalFields.stage` — the stage
 * the reader closed it FROM. Shared 1:1 with iOS/Android `OutreachClose`.
 */
export function OutreachClose({
  outreachId,
  stage,
  onClosed,
  startIcon,
  align = 'start',
  pageName,
  refresh,
}: {
  outreachId: string;
  /** The thread's current stage: hides the action on Done, and rides the BI event. */
  stage: OutreachStage;
  /** Fired once the write landed AND the re-read finished, so the surface can dismiss itself. */
  onClosed: () => void;
  /** The design's prefix slot — a 16px lucide glyph, `aria-hidden`. */
  startIcon?: ReactNode;
  /** `end` right-aligns the row, for the viewer where it sits just above the composer. */
  align?: 'start' | 'end';
  /** The src:198 page this press belongs to. Defaults to the tasks page. */
  pageName?: string;
  /** An extra re-read for the caller's own cache (e.g. one contact's entry). The contact-book stages and
   *  the bridge's outreach rows are always refreshed (`rereadAfterOwnerAction`). */
  refresh?: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (stage === 'done') return null;

  const close = async () => {
    if (busy) return;
    logPageClickBi(
      { ...TASKS_PAGE, ...(pageName ? { page_name: pageName } : {}), button_name: 'outreach_close' },
      // No entity id (rule 11): the thread is the entity, and it rides as `outreach_id`.
      { additionalFields: { outreach_id: outreachId, stage: stageBiValue(stage) } },
    );
    setBusy(true);
    setFailed(false);
    try {
      const res = await apiFetch(`${API_BASE}/api/outreach-complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outreachId }),
      });
      if (!res.ok) throw new Error(`complete ${res.status}`);
      // Re-read BEFORE dismissing, so the list the reader lands back on already shows the thread Done. The
      // same set every "Tell {agent}" host re-reads (`lib/owner-note-refresh.ts`): contact-book stages,
      // the bridge's outreach rows, and the caller's own read. Never rejects.
      await rereadAfterOwnerAction(refresh);
      onClosed();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <div className={align === 'end' ? `${styles.noReplyRow} ${styles.noReplyRowEnd}` : styles.noReplyRow}>
      {/* Announced, because the only other evidence of a failure is a surface that stayed open — which
          reads as a button that does nothing. ABOVE the button, where both phones put it. */}
      {failed ? (
        <p className={styles.failed} role="alert">
          {t('outreach.close.failed')}
        </p>
      ) : null}
      {/* `loading`, NOT `disabled`: the write takes a round trip, and a greyed-out button says
          "unavailable" where it should say "working". */}
      <TextButton size="small" startIcon={startIcon} onClick={close} loading={busy}>
        {t('outreach.close')}
      </TextButton>
    </div>
  );
}
